import React, { useState, useCallback, useMemo, useEffect } from "react";
import Header from "../../components/Header";
import SearchComponent from "../../components/Search";
import EmptyState from "../../components/EmptyState";
import PaginationComponentOne from "../../components/Pagination/Pagination-O";
import { debounce } from "lodash";
import { ContentCopy } from "@mui/icons-material";
import toast from "react-hot-toast";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { getAllExpiryAlert } from "../../redux/Inventory/inventoryActions";

const ExpiryAlertPage = () => {
  const [searchValue, setSearchValue] = useState("");
  const [tableFilter, setTableFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const dispatch = useDispatch();
  const {
    expiryAlertData,
    expiryAlertLoading,
    expiryAlertCurrentPage,
    expiryAlertTotalCount,
  } = useSelector((state) => state.inventory);

  // Function to fetch data with current state
  const fetchExpiryAlerts = useCallback(
    (params = {}) => {
      const requestParams = {
        page: params.page || currentPage,
        page_size: params.page_size || pageSize,
        search: params.search !== undefined ? params.search : searchValue,
        status:
          params.status !== undefined
            ? params.status
            : tableFilter !== "All"
            ? tableFilter
            : "",
      };

      dispatch(getAllExpiryAlert(requestParams));
    },
    [dispatch, currentPage, pageSize, searchValue, tableFilter]
  );

  // Initial load
  useEffect(() => {
    fetchExpiryAlerts();
  }, [dispatch, fetchExpiryAlerts]); // Only run on mount

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm) => {
        setCurrentPage(1);
        fetchExpiryAlerts({
          page: 1,
          search: searchTerm,
        });
      }, 500),
    [fetchExpiryAlerts]
  );

  const handleChange = useCallback(
    (e) => {
      const val = e.target.value;
      setSearchValue(val);
      debouncedSearch(val);
    },
    [debouncedSearch]
  );

  const handleCopyNumber = (number) => {
    navigator.clipboard.writeText(number);
    toast.success(`${number} copied successfully`);
  };

  const handlePageChange = (paginationData) => {
    let newPage = currentPage;
    let newPageSize = pageSize;

    if (typeof paginationData === "object") {
      if (paginationData.page !== undefined) {
        newPage = paginationData.page;
        setCurrentPage(newPage);
      }
      if (
        paginationData.pageSize !== undefined &&
        paginationData.pageSize !== pageSize
      ) {
        newPageSize = paginationData.pageSize;
        setPageSize(newPageSize);
        newPage = 1; // reset page
        setCurrentPage(1);
      }
    } else {
      newPage = paginationData;
      setCurrentPage(newPage);
    }

    // Fetch with new pagination parameters
    fetchExpiryAlerts({
      page: newPage,
      page_size: newPageSize,
    });
  };

  const handleFilterChange = (filter) => {
    setTableFilter(filter);
    setCurrentPage(1);
    fetchExpiryAlerts({
      page: 1,
      status: filter !== "All" ? filter : "",
    });
  };

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div>
      <Header text="Expiry Alert" />

      <div className="mt-5 bg-white rounded-[8px] border border-[#E1E6E1] px-4 col-span-3">
        {/* search section */}
        <div className="mt-4">
          <SearchComponent
            placeholder="Search Product Name, Stock ID(STK ID)"
            searchValue={searchValue}
            handleChange={handleChange}
          />
        </div>

        {/* All, Near Expiry, Expired Filters */}
        <div className="pb-3 w-full border-b border-[#E1E6E1]">
          <div className="text-sm font-semibold text-[#3E473F] border border-[#E1E6E1] mt-3 max-w-[576px] rounded-lg overflow-hidden flex">
            <button
              className={`px-3 py-2 text-center flex-1 transition-colors ${
                tableFilter === "All"
                  ? "bg-white rounded-lg"
                  : "bg-[#F6F7F6] hover:bg-[#EBEDEF]"
              }`}
              onClick={() => handleFilterChange("All")}
            >
              All
            </button>
            <button
              className={`px-3 py-2 text-center flex-1 transition-colors ${
                tableFilter === "near_expiry"
                  ? "bg-white rounded-lg"
                  : "bg-[#F6F7F6] hover:bg-[#EBEDEF]"
              }`}
              onClick={() => handleFilterChange("near_expiry")}
            >
              Near Expiry
            </button>
            <button
              className={`px-3 py-2 text-center flex-1 transition-colors ${
                tableFilter === "expired"
                  ? "bg-white rounded-lg"
                  : "bg-[#F6F7F6] hover:bg-[#EBEDEF]"
              }`}
              onClick={() => handleFilterChange("expired")}
            >
              Expired
            </button>
          </div>
        </div>

        <div className="overflow-x-auto relative py-4">
          {expiryAlertLoading ? (
            <div className="animate-pulse w-full px-4 mb-4">
              <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
            </div>
          ) : expiryAlertData && expiryAlertData.length === 0 ? (
            <EmptyState text="No expiry alerts found. Your inventory will appear here once products are near expiry or expired!" />
          ) : (
            <>
              <table className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1]">
                <thead className="bg-[#F6F7F6]">
                  <tr className="text-sm">
                    <th
                      scope="col"
                      className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tl-lg rounded-bl-lg"
                    >
                      Product Name
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 font-semibold text-[#5F6D60]"
                    >
                      Stock ID
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 font-semibold text-[#5F6D60]"
                    >
                      Expiry Date
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tr-lg rounded-br-lg"
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white border-[#E1E6E1] mt-2">
                  {expiryAlertData &&
                    expiryAlertData.map((item) => (
                      <tr
                        key={item.stock_id}
                        className="text-sm hover:bg-[#EDF7EE] border-b border-[#E1E6E1] text-center items-center"
                      >
                        <td className="px-4 py-4">
                          <div className="flex gap-1 items-center">
                            <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                              {item.product_name}
                            </p>
                            <span className="font-semibold text-lg">/</span>
                            <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                              {item.variant_data?.formulation ||
                                item.variant_data?.type ||
                                "N/A"}
                            </p>
                            <span className="font-semibold text-lg">/</span>
                            <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                              {item.variant_data?.value || "N/A"}
                              {item.variant_data?.unit || ""}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-[#344335]">
                          <p className="border w-fit flex items-center gap-1 py-0.5 px-1 border-[#E1E6E1] rounded-md font-medium">
                            {item.stock_id}
                            <ContentCopy
                              className="text-[#5F6D60] cursor-pointer hover:text-[#3E473F] transition-colors"
                              style={{ fontSize: "14px" }}
                              onClick={() => handleCopyNumber(item?.stock_id)}
                            />
                          </p>
                        </td>
                        <td className="px-4 py-4 text-[#5F6D60]">
                          {moment(item.expiry_date).format("DD-MM-YYYY")}
                        </td>
                        <td className="px-4 py-4 text-[#5F6D60]">
                          <p
                            className={`py-1 px-2 rounded-[4px] font-semibold w-fit ${
                              item.status === "Expired"
                                ? "text-[#B92020] border border-[#B92020]"
                                : item.status.includes("days")
                                ? "text-[#D4A407] border border-[#D4A407]"
                                : "text-[#2B8C34] border border-[#2B8C34]"
                            }`}
                          >
                            {item.status}
                          </p>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="border-t border-[#E1E6E1]">
                <PaginationComponentOne
                  totalItems={expiryAlertTotalCount}
                  page={expiryAlertCurrentPage}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpiryAlertPage;
